import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { env } from '@/lib/env';

import { editorAccessKeys } from './queryKeys';
import { reportEditorAccessProblem } from './report';

/** What the server says about its MCP endpoint (spec 236 FR-003). */
export type McpDiscovery =
  { status: 'on'; address: string } | { status: 'off' };

/** A discovery read that gave no answer, with what came back, for the report. */
export class McpDiscoveryError extends Error {
  constructor(
    /** `status`: refused with something other than 404; `shape`: a 200 whose body is not the document. */
    readonly reason: 'status' | 'shape',
    readonly status: number,
  ) {
    super(
      reason === 'status'
        ? `MCP discovery answered ${String(status)}`
        : 'MCP discovery answered with no usable resource',
    );
    this.name = 'McpDiscoveryError';
  }
}

/**
 * The part of the document this page uses. `resource` ends up in a command a
 * person pastes into a terminal, so it must be an http(s) URL made only of
 * characters a URL needs — nothing a shell would read as a quote, a separator
 * or a substitution.
 */
const discoveryDocument = z.object({
  resource: z
    .url({ protocol: /^https?$/ })
    .regex(/^[\w\-.~:/%]+$/, 'not a plain URL'),
});

/**
 * The discovery document an MCP editor reads first (RFC 9728), on the API's
 * origin. Read with plain `fetch` and no credentials: the backend serves it
 * anonymously and keeps it out of `openapi.json`, both pinned by its
 * tests/http/test_mcp_endpoint.py — so it is not in the generated client. Its
 * `resource` is the address to give an editor — the server's own word for it,
 * right in every environment.
 *
 * A 404 is an answer: with MCP switched off the backend registers neither this
 * route nor `/mcp` (`test_off_by_default_both_routes_are_absent`). A 404 from
 * anything in front of it would read the same, and nothing here can tell the
 * two apart. The likely routing fault does not look like that, though: a
 * gateway without its MCP route (product-x-next-local-dev k8s/gateway.yaml)
 * hands this path to the SPA catch-all, whose 200 `index.html` fails the shape
 * check below — reported, and "could not be checked", not "off". Anything
 * other than a 404 or the document is a failure, reported and offered again.
 */
export async function fetchMcpDiscovery(
  signal?: AbortSignal,
  /**
   * Whether a failure is reported. The AI tools page reports it, once a visit;
   * the hints around the app (spec 248) ask on every session, where a gateway
   * without the route would report from every one of them.
   */
  { report = true }: { report?: boolean } = {},
): Promise<McpDiscovery> {
  let status: number | undefined;
  try {
    const origin = new URL(env.VITE_API_BASE_URL).origin;
    const response = await fetch(
      `${origin}/.well-known/oauth-protected-resource/mcp`,
      { signal },
    );
    status = response.status;
    if (response.status === 404) return { status: 'off' };
    if (!response.ok) throw new McpDiscoveryError('status', response.status);
    // A 200 that is not JSON (a gateway's fallback page) is a shape failure too.
    const body: unknown = await response.json().catch(() => undefined);
    const parsed = discoveryDocument.safeParse(body);
    if (!parsed.success) throw new McpDiscoveryError('shape', response.status);
    return { status: 'on', address: parsed.data.resource };
  } catch (error) {
    // Not when the page has gone away: the abort is TanStack's, not a fault.
    if (report && !signal?.aborted) {
      reportEditorAccessProblem('could not check whether MCP is on', {
        // Absent when there was no response at all — itself the diagnosis.
        status,
        reason: error instanceof McpDiscoveryError ? error.reason : 'failed',
        error,
      });
    }
    throw error;
  }
}

export function useMcpDiscovery() {
  return useQuery({
    queryKey: editorAccessKeys.discovery(),
    queryFn: ({ signal }) => fetchMcpDiscovery(signal),
    // Whether the server is on is a deployment setting, so it is not expected
    // to change while the page is open; a reload reads it again.
    staleTime: 5 * 60 * 1000,
    // The page offers Try again, and every failure is reported, so a blip is
    // one click away and still on record.
    retry: false,
  });
}
